import { useState, useEffect, useMemo, useRef, useCallback, useId } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'
import { addDays } from 'date-fns'
import { clsx } from 'clsx'
import debounce from 'lodash/debounce'

import { Popover, IconButton } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

import { OnTrackRing } from './components/OnTrackRing'
import { StreakWidgetContent } from './components/StreakWidgetContent'
import { useWeeklyPostingStreak, usePreviousStreakStatus } from './hooks'
import type { WeeklyPostingStreakData } from './hooks'
import {
  getAtRiskStreakPopupDismissedObjectId,
  shouldShowAtRiskStreakPopup,
} from './atRiskStreakPopup'
import StreakIcon from './images/StreakIcon'

import styles from './Streaks.module.css'

type StreakStatus = NonNullable<
  WeeklyPostingStreakData['weeklyPostingStreak']
>['status']

const statusClassNames: Partial<Record<StreakStatus, string>> = {
  AtRisk: styles.atrisk,
  OnTrack: styles.ontrack,
  Hit: styles.hit,
}

const DISMISS_AT_RISK_STREAK_POPUP_EXPIRY_DAYS = 14

const DismissAtRiskStreakPopup = graphql(/* GraphQL */ `
  mutation DismissAtRiskStreakPopup(
    $organizationId: String!
    $dismissedObject: DismissedObjectInput!
  ) {
    addOrUpdateDismissedObject(
      organizationId: $organizationId
      dismissedObject: $dismissedObject
    ) {
      ... on AddOrUpdateDismissedObjectResponse {
        success
      }
      ... on AddOrUpdateDismissedObjectError {
        cause
        userFriendlyMessage
      }
    }
  }
`)

const stopPropagation = (e: React.MouseEvent<HTMLButtonElement>): void => {
  e.stopPropagation()
}

export const StreaksWidget = (): JSX.Element | null => {
  const { account } = useAccount()
  const headingId = useId()
  const initialFocusRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [dismissAtRiskStreakPopup] = useMutation(DismissAtRiskStreakPopup)
  const shownAtRiskPopupIds = useRef(new Set<string>())
  const currentAtRiskPopupDismissedObjectId = useRef<string | null>(null)
  const organization = account?.currentOrganization
  const organizationId = organization?.id

  const {
    data: { weeklyPostingStreak: streak } = {},
    refetch: refetchStreakQuery,
  } = useWeeklyPostingStreak(organizationId ?? undefined)
  const initialStreakStatus = usePreviousStreakStatus(streak?.status)

  // Create debounced refetch function that persists across renders
  const debouncedRefetch = useMemo(
    () =>
      debounce(() => {
        refetchStreakQuery().catch((error: Error) => {
          const unexpectedError = new Error(
            error?.message || 'Failed to refetch streak',
          )
          logError(unexpectedError, { metaData: { error } })
        })
      }, 5000),
    [refetchStreakQuery],
  )

  // Listen for post creation events to refetch the streak query with each new post
  useEffect(() => {
    const STREAK_UPDATE_EVENT_KEY = 'buffer.streak.update'
    window.addEventListener(STREAK_UPDATE_EVENT_KEY, debouncedRefetch)

    return (): void => {
      debouncedRefetch.cancel()
      window.removeEventListener(STREAK_UPDATE_EVENT_KEY, debouncedRefetch)
    }
  }, [debouncedRefetch])

  useEffect(() => {
    if (streak) {
      if (
        (initialStreakStatus === 'Started' ||
          initialStreakStatus === 'Missed') &&
        (streak.status === 'OnTrack' || streak.status === 'Hit')
      ) {
        setOpen(true)
      }

      // Auto-open for celebration states
      if (
        streak.userStreakState === 'ChannelConnectionStreakCelebration' ||
        streak.userStreakState === 'PerfectMonthCelebration'
      ) {
        setOpen(true)
      }
    }
  }, [streak, initialStreakStatus])

  const dismissCurrentAtRiskPopup = useCallback((): void => {
    const dismissedObjectId = currentAtRiskPopupDismissedObjectId.current
    if (!dismissedObjectId || !organizationId || !organization) {
      return
    }

    currentAtRiskPopupDismissedObjectId.current = null

    void dismissAtRiskStreakPopup({
      variables: {
        organizationId,
        dismissedObject: {
          id: dismissedObjectId,
          expiresAt: addDays(
            new Date(),
            DISMISS_AT_RISK_STREAK_POPUP_EXPIRY_DAYS,
          ).toISOString(),
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify(organization),
          fields: {
            dismissedObjects(existingObjects = []) {
              if (
                existingObjects.some(
                  (entry: { id?: string }) => entry?.id === dismissedObjectId,
                )
              ) {
                return existingObjects
              }
              return [
                ...existingObjects,
                { __typename: 'DismissedObject', id: dismissedObjectId },
              ]
            },
          },
        })
      },
    })
  }, [dismissAtRiskStreakPopup, organization, organizationId])

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      setOpen(nextOpen)

      if (!nextOpen) {
        dismissCurrentAtRiskPopup()
      }
    },
    [dismissCurrentAtRiskPopup],
  )

  useEffect(() => {
    const timezone = account?.timezone
    const dismissedObjectIds =
      organization?.dismissedObjects?.map(
        (dismissedObject) => dismissedObject.id,
      ) ?? []

    const shouldShowPopup = shouldShowAtRiskStreakPopup({
      streak,
      accountCreatedAt: account?.createdAt,
      organizationId,
      timezone,
      dismissedObjectIds,
    })

    if (!shouldShowPopup || !organizationId || !timezone) {
      return
    }

    const dismissedObjectId = getAtRiskStreakPopupDismissedObjectId({
      organizationId,
      timezone,
    })
    if (
      !dismissedObjectId ||
      shownAtRiskPopupIds.current.has(dismissedObjectId)
    ) {
      return
    }

    shownAtRiskPopupIds.current.add(dismissedObjectId)
    currentAtRiskPopupDismissedObjectId.current = dismissedObjectId
    setOpen(true)
  }, [
    account?.createdAt,
    account?.timezone,
    organization,
    organizationId,
    streak,
  ])

  if (!streak) return null

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <IconButton
          label="View Streak"
          tooltip="Your current streak"
          variant="tertiary"
          size="medium"
          onClick={stopPropagation}
        >
          <>
            {streak.status === 'OnTrack' && (
              <OnTrackRing className={styles.onTrackRing} />
            )}
            <span className={styles.iconWrapper}>
              <StreakIcon />
              <StreakWidgetCount streak={streak} />
            </span>
          </>
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        align="center"
        arrow
        className={styles.popover}
        aria-labelledby={headingId}
        onOpenAutoFocus={(event): void => {
          if (initialFocusRef.current) {
            event.preventDefault()
            initialFocusRef.current.focus()
          }
        }}
      >
        <StreakWidgetContent
          streak={streak}
          headingId={headingId}
          initialFocusRef={initialFocusRef}
          onClose={(): void => handleOpenChange(false)}
        />
      </Popover.Content>
    </Popover>
  )
}

type StreakWidgetCountProps = {
  className?: string
  streak: NonNullable<WeeklyPostingStreakData['weeklyPostingStreak']>
}

export const StreakWidgetCount = ({
  streak,
  className,
}: StreakWidgetCountProps): JSX.Element | null => {
  const statusClass = statusClassNames[streak.status]
  const showStreakCount = !(streak.status === 'Started' && streak.count === 0)

  if (!showStreakCount) return null

  return (
    <div className={clsx(styles.widgetCount, statusClass, className)}>
      {streak.count}
    </div>
  )
}
